import { formatMoney, parseMoney, priceChart, priceCheckHint, watchStatus, type Item } from '@closetly/core';
import { useQueryClient } from '@tanstack/react-query';
import { useState, type Ref } from 'react';
import { checkPrice, historyKey, itemsKey, useItems, usePriceHistory } from '../lib/queries';
import { PushControls } from './NotifySheet';

interface Props {
  /** The piece as saved, null while creating one. */
  item: Item | null;
  watch: boolean;
  onWatch: (on: boolean) => void;
  target: string;
  onTarget: (value: string) => void;
  targetRef: Ref<HTMLInputElement>;
  targetError: string;
  /** The form's price and link right now. */
  price: string;
  link: string;
  linkReady: boolean;
  /** A check found a new price: the form takes it over. */
  onChecked: (priceCents: number) => void;
}

const CHART_W = 240;
const CHART_H = 36;

/** "Preis beobachten" in the item form: switch, target price, status, history. */
export function PriceWatch({
  item,
  watch,
  onWatch,
  target,
  onTarget,
  targetRef,
  targetError,
  price,
  link,
  linkReady,
  onChecked,
}: Props) {
  const targetCents = parseMoney(target);
  const priceCents = parseMoney(price);
  const fmt = (c: number) => formatMoney(c, item?.currency || undefined);

  let targetHint = 'Ohne Zielpreis meldet sich Closetly bei jedem günstigeren Preis.';
  if (targetCents && priceCents && targetCents >= priceCents)
    targetHint = 'Der Preis liegt schon bei deinem Ziel oder darunter.';
  else if (targetCents) targetHint = `Closetly meldet sich, sobald es ${fmt(targetCents)} oder weniger kostet.`;

  return (
    <div className="watch" role="group" aria-labelledby="f-watch-label">
      <label className="check">
        <input
          type="checkbox"
          checked={watch}
          disabled={!linkReady && !watch}
          onChange={(e) => onWatch(e.target.checked)}
        />{' '}
        <span id="f-watch-label">Preis beobachten</span>
      </label>
      {!watch && !linkReady && <p className="hint">Dafür braucht das Teil einen Link zum Shop.</p>}

      {watch && (
        <>
          <div className="field">
            <label htmlFor="f-target">Zielpreis (€, optional)</label>
            <input
              id="f-target"
              ref={targetRef}
              value={target}
              onChange={(e) => onTarget(e.target.value)}
              inputMode="decimal"
              placeholder="39,90"
              autoComplete="off"
            />
            {targetError ? (
              <div className="err" role="alert">
                {targetError}
              </div>
            ) : (
              <p className="hint">{targetHint}</p>
            )}
          </div>
          {item && <WatchState item={item} link={link} onChecked={onChecked} />}
          <PushControls compact />
        </>
      )}
    </div>
  );
}

/** What the server found so far, for a piece that is saved as watched with this link. */
function WatchState({ item, link, onChecked }: { item: Item; link: string; onChecked: (cents: number) => void }) {
  const qc = useQueryClient();
  // the server changes the piece while it checks; show its latest state
  const live = useItems(item.owner).data?.find((i) => i.id === item.id) ?? item;
  const saved = live.price_watch && live.link && live.link === link.trim() ? live : null;
  const history = usePriceHistory(saved?.id ?? null);
  const [checking, setChecking] = useState(false);
  const [result, setResult] = useState('');
  if (!saved) return null;

  const fmt = (c: number) => formatMoney(c, saved.currency || undefined);
  const status = watchStatus(saved);
  const chart = priceChart(history.data ?? [], CHART_W, CHART_H);

  async function check() {
    setChecking(true);
    setResult('');
    try {
      const r = await checkPrice(saved!.id);
      if (r.price_cents) {
        onChecked(r.price_cents);
        setResult(`Gerade ${fmt(r.price_cents)}.`);
      } else setResult(r.error ? priceCheckHint(r.error) : '');
      await Promise.all([
        qc.invalidateQueries({ queryKey: itemsKey(saved!.owner) }),
        qc.invalidateQueries({ queryKey: historyKey(saved!.id) }),
      ]);
    } catch {
      setResult(navigator.onLine ? 'Die Prüfung hat nicht geklappt.' : 'Dafür braucht es eine Verbindung.');
    } finally {
      setChecking(false);
    }
  }

  return (
    <>
      {status && <p className={`hint watch-status${status.tone === 'warn' ? ' warn' : ''}`}>{status.text}</p>}
      {chart && (
        <figure className="spark">
          <svg viewBox={`0 0 ${CHART_W} ${CHART_H}`} preserveAspectRatio="none" role="img" aria-label="Preisverlauf">
            <path d={chart.path} />
          </svg>
          <figcaption>
            Günstigster Preis bisher {fmt(chart.lowCents)} · am Anfang {fmt(chart.firstCents)}
          </figcaption>
        </figure>
      )}
      {!saved.bought && (
        <div className="watch-check">
          <button type="button" className="btn" disabled={checking} onClick={check}>
            {checking ? 'Prüfe…' : 'Jetzt prüfen'}
          </button>
          <span className="hint" role="status" aria-live="polite">
            {result}
          </span>
        </div>
      )}
    </>
  );
}
